'use client'

import dynamic from 'next/dynamic'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useI18n } from './i18n.js'

const GlobalUserInfoPopover = dynamic(
  () => import('../app/forum/features/profile/components/UserInfoPopover'),
  { ssr: false },
)
const GlobalSubscriptionsPopover = dynamic(
  () => import('../app/forum/features/subscriptions/components/SubscriptionsPopover'),
  { ssr: false },
)
const GlobalForumStyles = dynamic(() => import('../app/forum/styles/ForumStyles'), { ssr: false })
const GLOBAL_USER_INFO_EXIT_HOLD_MS = 440

function isForumRoute() {
  try {
    return /^\/forum(?:\/|$)/u.test(String(window.location?.pathname || ''))
  } catch {
    return false
  }
}

export default function UserInfoPopoverHost() {
  const { t } = useI18n()
  const anchorRef = useRef(null)
  const userInfoOpenRef = useRef(false)
  const userInfoUserIdRef = useRef('')
  const userInfoExitTimerRef = useRef(null)
  const [userInfo, setUserInfo] = useState({ open: false, userId: '', preview: null })
  const [subscriptions, setSubscriptions] = useState({ open: false, userId: '', initialMode: 'followers' })

  const clearUserInfoExitTimer = useCallback(() => {
    if (!userInfoExitTimerRef.current) return
    try { window.clearTimeout(userInfoExitTimerRef.current) } catch {}
    userInfoExitTimerRef.current = null
  }, [])

  const closeUserInfo = useCallback(() => {
    clearUserInfoExitTimer()
    userInfoOpenRef.current = false
    userInfoUserIdRef.current = ''
    // Keep the global host + anchor alive long enough for UserInfoPopover's own exit motion.
    setUserInfo((current) => ({ ...current, open: false }))
    if (typeof window === 'undefined') {
      anchorRef.current = null
      setUserInfo({ open: false, userId: '', preview: null })
      return
    }
    userInfoExitTimerRef.current = window.setTimeout(() => {
      anchorRef.current = null
      setUserInfo({ open: false, userId: '', preview: null })
      userInfoExitTimerRef.current = null
    }, GLOBAL_USER_INFO_EXIT_HOLD_MS)
  }, [clearUserInfoExitTimer])

  const closeSubscriptions = useCallback(() => {
    setSubscriptions({ open: false, userId: '', initialMode: 'followers' })
  }, [])

  const openUserInfo = useCallback((userId, anchor, preview = null) => {
    const uid = String(userId || '').trim()
    if (!uid || !anchor) return
    clearUserInfoExitTimer()
    const sameOpenTarget = userInfoOpenRef.current && userInfoUserIdRef.current === uid && anchorRef.current === anchor
    if (sameOpenTarget) {
      closeUserInfo()
      return
    }
    anchorRef.current = anchor
    userInfoOpenRef.current = true
    userInfoUserIdRef.current = uid
    setUserInfo({ open: true, userId: uid, preview: preview && typeof preview === 'object' ? preview : null })
  }, [clearUserInfoExitTimer, closeUserInfo])

  useEffect(() => () => clearUserInfoExitTimer(), [clearUserInfoExitTimer])

  useEffect(() => {
    const onOpen = (event) => {
      // The Forum route already owns this exact event through useForumSessionShell.
      if (isForumRoute()) return
      const detail = event?.detail || {}
      openUserInfo(detail.userId || detail.rawUserId, detail.anchor || detail.anchorEl, detail.userPreview || null)
    }
    window.addEventListener('forum:user-info-open', onOpen)
    return () => window.removeEventListener('forum:user-info-open', onOpen)
  }, [openUserInfo])

  const openProfileBranch = useCallback((mode, payload = {}) => {
    const userId = String(payload?.userId || '').trim()
    if (!userId) return
    const detail = {
      mode: mode === 'topics' ? 'topics' : 'posts',
      userId,
      nickname: String(payload?.nickname || userInfo.preview?.nickname || '').trim(),
    }
    try {
      window.sessionStorage?.setItem('ql7_forum_profile_branch_after_navigation_v1', JSON.stringify(detail))
    } catch {}
    closeSubscriptions()
    closeUserInfo()
    const params = new URLSearchParams({ profileMode: detail.mode, profileUser: detail.userId })
    if (detail.nickname) params.set('profileNick', detail.nickname)
    window.location.assign(`/forum?${params.toString()}`)
  }, [closeSubscriptions, closeUserInfo, userInfo.preview?.nickname])

  const openSubscriptions = useCallback(({ userId, initialMode = 'followers' } = {}) => {
    const uid = String(userId || '').trim()
    if (!uid) return
    closeUserInfo()
    setSubscriptions({ open: true, userId: uid, initialMode: initialMode === 'following' ? 'following' : 'followers' })
  }, [closeUserInfo])

  // QL7_GLOBAL_QUANTUM_FAMILY_STACK_V1: keep Quantum Family mounted while inspecting a member.
  const openUserInfoFromSubscriptions = useCallback((userId, anchor, preview = null) => {
    openUserInfo(userId, anchor, preview)
  }, [openUserInfo])

  const closeGlobalProfileAction = useCallback(() => {
    closeSubscriptions()
    closeUserInfo()
  }, [closeSubscriptions, closeUserInfo])

  if (!userInfo.open && !subscriptions.open && !userInfo.userId) return null

  return (
    <>
      <GlobalForumStyles />
      <GlobalSubscriptionsPopover
        open={subscriptions.open}
        userId={subscriptions.userId}
        initialMode={subscriptions.initialMode}
        onClose={closeSubscriptions}
        onOpenUserInfo={openUserInfoFromSubscriptions}
        t={t}
      />
      <GlobalUserInfoPopover
        anchorRef={anchorRef}
        open={userInfo.open}
        onClose={closeUserInfo}
        rawUserId={userInfo.userId}
        userPreview={userInfo.preview}
        t={t}
        onOpenUserPosts={(payload) => openProfileBranch('posts', payload)}
        onOpenUserTopics={(payload) => openProfileBranch('topics', payload)}
        onOpenSubscriptions={openSubscriptions}
        onOpenDirectMessage={closeGlobalProfileAction}
        onOpenGiftFlow={closeGlobalProfileAction}
      />
    </>
  )
}
